import React from "react";
import { History } from "lucide-react";
import dayjs from "dayjs";
import { cn } from "@/lib/utils";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AiConversation } from "../../../models/AiAssistant";

interface AiConversationListProps {
	conversations: AiConversation[];
	activeConversationId?: string;
	onSelect: (conversationId: string) => void;
	className?: string;
}

/**
 * History dropdown in the sidebar header. Shows the user's conversations
 * newest first with title (or first message) and when they were last active.
 */
const AiConversationList: React.FC<AiConversationListProps> = ({
	conversations,
	activeConversationId,
	onSelect,
	className,
}) => {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					title="Tidigare konversationer"
					className={cn(
						"inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-neutral-100 hover:text-foreground",
						className
					)}
				>
					<History className="h-4 w-4" />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-72">
				<DropdownMenuLabel>Tidigare konversationer</DropdownMenuLabel>
				<DropdownMenuSeparator />
				{conversations.length === 0 && (
					<div className="px-2 py-3 text-sm text-muted-foreground">Inga konversationer ännu.</div>
				)}
				{conversations.map((conversation) => (
					<DropdownMenuItem
						key={conversation.id}
						onSelect={() => onSelect(conversation.id)}
						className={cn(
							"flex flex-col items-start gap-0.5",
							conversation.id === activeConversationId && "bg-neutral-100"
						)}
					>
						<span className="line-clamp-1 w-full text-sm">
							{conversation.title || firstUserText(conversation) || "Ny konversation"}
						</span>
						<span className="text-xs text-foreground-subtle">
							{dayjs(conversation.updated).format("D MMM HH:mm")} · {conversation.turns.length}{" "}
							meddelanden
						</span>
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

function firstUserText(conversation: AiConversation) {
	return conversation.turns.find((turn) => turn.role === "user")?.text;
}

export default AiConversationList;
